import { ShipInfo } from '@ais-ui/websockets';
import {
  NextPortArrivalWithDate,
  useNextportShipImoGet,
  usePortGet,
} from '@ais-ui/api';
import {
  Button,
  LoadingSpinner,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import React, { useCallback, useEffect, useMemo } from 'react';
import { PiMagnifyingGlassPlus } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { GeoJson, Geometry } from '@ais-ui/api-spec/shapes';
import { PortExtended } from '@ais-ui/api-spec/portserver';
import { fromLonLat } from 'ol/proj';
import { Coordinate } from 'ol/coordinate';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { RouteButton } from '../../utils/RouteButton';

interface NextPortProps {
  shipInfo: ShipInfo;
}

interface NextPortItemProps {
  nextport: NextPortArrivalWithDate;
  shipInfo: ShipInfo;
  setPorts: React.Dispatch<React.SetStateAction<PortExtended[]>>;
}

const NextPortItem = ({ shipInfo, nextport, setPorts }: NextPortItemProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { map } = useOpenLayersContext();

  const { data: port, isLoading } = usePortGet(
    nextport?.locode ? nextport.locode : ''
  );

  useEffect(() => {
    if (!port) {
      return;
    }
    setPorts((prev) => [...prev, port]);
  }, [port, setPorts]);

  const zoomToPoint = useCallback(
    (point: Coordinate) => {
      map.getView().setCenter(point);

      map.getView().setZoom(Math.min(map.getView().getZoom() || 16));
    },
    [map]
  );

  const onZoomToPort = () => {
    if (!port || !port.position) {
      return;
    }
    const transformedCoordinates = fromLonLat(
      port.position.coordinates as [number, number]
    );

    zoomToPoint(transformedCoordinates);
  };

  if (isLoading) {
    return <LoadingSpinner size="small"></LoadingSpinner>;
  }

  return (
    <div className="w-full flex space-x-4 py-4">
      <div className="flex space-x-2 items-center">
        <RouteButton
          mmsi={shipInfo.mmsi}
          shipName={shipInfo.name}
          nextport={nextport}
          portName={port?.name}
        ></RouteButton>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" onClick={onZoomToPort}>
              <PiMagnifyingGlassPlus className="h-6 w-6"></PiMagnifyingGlassPlus>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Zoom to port</TooltipContent>
        </Tooltip>
        <Separator orientation="vertical" className="h-8 bg-gray-300" />
      </div>
      <div>
        <div className="flex flex-col space-y-1.5 justify-center">
          <div className="flex space-x-2 items-center">
            <p className="text-sm text-left font-medium leading-none group-hover:underline">
              {port ? port.name : '-'}
            </p>
            <Separator orientation="vertical" className="h-4 bg-gray-300" />
            <p className="text-sm text-left font-medium leading-none group-hover:underline">
              {nextport.locode}
            </p>
          </div>

          <div className="flex space-x-2 items-center">
            <span
              className={cn(
                'block w-2 h-2 rounded-full',
                (nextport.probability ?? -1) === -1 && 'bg-gray-500',
                (nextport.probability ?? -1) >= 0.5 && 'bg-green-500',
                (nextport.probability ?? -1) < 0.5 &&
                  (nextport.probability ?? -1) > 0.2 &&
                  'bg-yellow-500',
                (nextport.probability ?? -1) <= 0.2 && 'bg-red-500'
              )}
            ></span>
            <p className="text-sm text-left text-muted-foreground">
              {nextport.probability
                ? `${(nextport.probability * 100).toFixed(1)} %`
                : '-'}
            </p>
            <Separator orientation="vertical" className="h-4 bg-gray-300" />
            <p className="text-sm text-left text-muted-foreground">
              {formatDateTimeWithTimeZone(nextport.eta)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NextPortPanel = ({ shipInfo }: NextPortProps) => {
  const { messages } = useLocalization();
  const { addLayer, doesLayerExist, zoomToLayer, getLayerById } = useLayers();

  const { data, isLoading, isError } = useNextportShipImoGet(shipInfo.imo);
  const [ports, setPorts] = React.useState<PortExtended[]>([]);

  const id = useMemo(() => `ports-nextport-${shipInfo.mmsi}`, [shipInfo.mmsi]);
  const name = useMemo(() => `${shipInfo.name} nextports`, [shipInfo.name]);

  useEffect(() => {
    const layer = getLayerById(id);

    if (layer) {
      const source = (layer.layer as VectorLayer<Feature>).getSource();

      // Ensure source is a VectorSource before modifying
      if (source && source instanceof VectorSource) {
        source.clear();
        const newFeatures = ports
          .filter((port) => port.position && port.position.coordinates)
          .map((port) => {
            return new Feature({
              geometry: new Point(
                fromLonLat(
                  (port.position as Geometry).coordinates as [number, number]
                )
              ),
              id: port?.id ? port.id.toString() : 'unknown port',
              name: port.name,
              locode: port.locode,
            });
          });

        // Add new features to the existing source
        source.addFeatures(newFeatures);
      }
    }
  }, [getLayerById, id, ports]);

  useEffect(() => {
    if (ports.length > 0 && !doesLayerExist(id)) {
      const portsGeoJson: GeoJson = {
        type: 'FeatureCollection',
        properties: {
          id: id,
          name: name,
        },
        features: [],
      };
      const layer = shapeToVectorLayer(portsGeoJson);

      addLayer(layer, name, true, true, true, undefined, id);
    }
  }, [addLayer, doesLayerExist, id, name, ports]);

  if (isLoading) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  return (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium mb-4">Next ports</h3>
          <div className="flex flex-col space-y-1 border rounded-lg p-4 shadow-sm">
            {data?.records?.map((nextport, index) => (
              <NextPortItem
                key={index}
                shipInfo={shipInfo}
                nextport={nextport}
                setPorts={setPorts}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
